/**
 * The v1 "authentic" render backend: executes a `DrawOp` list onto a logical
 * 320×200 canvas at 1:1, then blits that canvas to the visible one with
 * nearest-neighbour filtering, integer horizontal scale and the 1:1.2 VGA
 * pixel aspect. Palette indices become CSS colours only here — the draw list
 * itself stays index-based so another backend can reinterpret it.
 */

import { type DrawOp, type FieldStyle, fieldIndex, LOGICAL_HEIGHT, LOGICAL_WIDTH, type Span } from "./draw-ops.ts";
import { computePresentation, type Presentation, type ShellViewport } from "./present.ts";
import { paletteCss, paletteRgb } from "./ps-palette.ts";
import { type ShellAssets, tintedAtlas } from "./shell-assets.ts";
import { glyphFor } from "./text-metrics.ts";

export interface LogicalSurface {
  readonly canvas: HTMLCanvasElement;
  readonly context: CanvasRenderingContext2D;
}

export function createLogicalSurface(
  /**
   * Keep an alpha channel, so anything the frame does not paint stays clear.
   *
   * `false` for every screen in the shell — an opaque backing store is cheaper
   * and every one of them fills the frame anyway. `true` only for G38's `ESC`
   * board, which is composited over the cockpit: without it the *logical*
   * canvas is opaque black, and blitting it lays a black letterbox over the
   * aeroplane no matter what the visible canvas's own alpha says. (Found by
   * screenshot, which is the only way this one shows up: every unit test
   * passed and the board itself drew perfectly.)
   */
  transparent = false,
): LogicalSurface {
  const canvas = document.createElement("canvas");
  canvas.width = LOGICAL_WIDTH;
  canvas.height = LOGICAL_HEIGHT;
  const context = canvas.getContext("2d", { alpha: transparent, willReadFrequently: false });
  if (!context) throw new Error("shell: 2D context unavailable for the logical surface");
  context.imageSmoothingEnabled = false;
  return { canvas, context };
}

function paintField(surface: LogicalSurface, assets: ShellAssets, style: FieldStyle, spans: readonly Span[]): void {
  for (const span of spans) {
    if (span.w <= 0) continue;
    const row = new ImageData(span.w, 1);
    for (let i = 0; i < span.w; i += 1) {
      const [r, g, b] = paletteRgb(assets.palette, fieldIndex(style, span.x + i, span.y));
      row.data[i * 4] = r;
      row.data[i * 4 + 1] = g;
      row.data[i * 4 + 2] = b;
      row.data[i * 4 + 3] = 255;
    }
    surface.context.putImageData(row, span.x, span.y);
  }
}

function paintSprite(surface: LogicalSurface, assets: ShellAssets, op: Extract<DrawOp, { kind: "sprite" }>): void {
  const sheet = assets.images.get(op.sheet);
  const frame = assets.sprites.get(op.sheet)?.[op.frame];
  if (!sheet || !frame) return;
  const { context } = surface;
  context.save();
  if (op.flipH) {
    context.translate(op.x + frame.width, op.y);
    context.scale(-1, 1);
    context.drawImage(sheet, frame.x, frame.y, frame.width, frame.height, 0, 0, frame.width, frame.height);
  } else {
    context.drawImage(sheet, frame.x, frame.y, frame.width, frame.height, op.x, op.y, frame.width, frame.height);
  }
  context.restore();
}

function paintText(surface: LogicalSurface, assets: ShellAssets, op: Extract<DrawOp, { kind: "text" }>): void {
  const atlas = assets.fonts.get(op.font);
  const image = tintedAtlas(assets, op.font, op.style);
  if (!atlas || !image) return;
  let pen = op.x;
  for (const character of op.text) {
    const glyph = glyphFor(atlas, character.codePointAt(0) ?? 0x20);
    if (!glyph) continue;
    if (glyph.width > 0) {
      surface.context.drawImage(
        image,
        glyph.x,
        glyph.y,
        glyph.width,
        glyph.height + 1,
        pen,
        op.y,
        glyph.width,
        glyph.height + 1,
      );
    }
    pen += glyph.width;
  }
}

export function paintOps(surface: LogicalSurface, assets: ShellAssets, ops: readonly DrawOp[]): void {
  const { context } = surface;
  for (const op of ops) {
    switch (op.kind) {
      case "image": {
        const image = assets.images.get(op.asset);
        if (image) context.drawImage(image, op.x, op.y);
        break;
      }
      case "fill":
        context.fillStyle = paletteCss(assets.palette, op.color);
        context.fillRect(op.rect.x, op.rect.y, op.rect.w, op.rect.h);
        break;
      case "field":
        paintField(surface, assets, op.style, op.spans);
        break;
      case "sprite":
        paintSprite(surface, assets, op);
        break;
      case "text":
        paintText(surface, assets, op);
        break;
    }
  }
}

export function clearLogical(surface: LogicalSurface, color = "#000000"): void {
  surface.context.fillStyle = color;
  surface.context.fillRect(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT);
}

/**
 * Wipes the logical frame to *nothing* rather than to black.
 *
 * For the one surface that is composited over a live picture instead of
 * replacing it: G38's in-flight `ESC` board, which the original draws over the
 * cockpit (`docs/game/ui-reference/dialog-mission-not-complete.png`). An opaque
 * surface would black out the aeroplane the player is deciding what to do with.
 *
 * `paint` does not clear for an ordinary screen, because such a screen's own
 * background op covers the frame. A transparent one has no background op, so
 * without this every frame would draw over the last.
 */
export function clearLogicalTransparent(surface: LogicalSurface): void {
  surface.context.clearRect(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT);
}

/** Blits the logical frame to the visible canvas, letterboxed. Returns the transform so input can be inverted through it. */
export function present(
  target: HTMLCanvasElement,
  surface: LogicalSurface,
  viewport: ShellViewport,
  /** Composite over whatever is behind the element instead of laying black down first — see `clearLogicalTransparent`. */
  transparent = false,
): Presentation {
  const presentation = computePresentation(viewport);
  // `alpha` is fixed at the first `getContext` for the life of the element, so
  // the flag has to be decided by the surface that owns the canvas rather than
  // per call. It is: `createShellSurface` passes the same value every time.
  const context = target.getContext("2d", { alpha: transparent });
  if (!context) return presentation;
  context.imageSmoothingEnabled = false;
  if (transparent) {
    context.clearRect(0, 0, viewport.width, viewport.height);
  } else {
    context.fillStyle = "#000000";
    context.fillRect(0, 0, viewport.width, viewport.height);
  }
  context.drawImage(
    surface.canvas,
    0,
    0,
    LOGICAL_WIDTH,
    LOGICAL_HEIGHT,
    presentation.originX,
    presentation.originY,
    presentation.destWidth,
    presentation.destHeight,
  );
  return presentation;
}
